import { PROMISE_SETTLED_STATUS } from "../constants";
import {
  emptySlot,
  type AsyncSlot,
  type FinancialSituation,
  type MediaMonitorData,
  type PriceAlert,
  type StockPageData,
  type StockPayload,
  type StockRecommendation,
  type StockRecommendationsPayload,
  type StockTag
} from "../types";

export const STOCK_PAGE_ACTION = {
  RESET_ENTRY: "RESET_ENTRY",
  PRIMARY_LOADING: "PRIMARY_LOADING",
  PRIMARY_SETTLED: "PRIMARY_SETTLED",
  SECONDARY_LOADING: "SECONDARY_LOADING",
  SECONDARY_SETTLED: "SECONDARY_SETTLED",
  FINANCIAL_SITUATION_LOADING: "FINANCIAL_SITUATION_LOADING",
  FINANCIAL_SITUATION_SUCCEEDED: "FINANCIAL_SITUATION_SUCCEEDED",
  FINANCIAL_SITUATION_FAILED: "FINANCIAL_SITUATION_FAILED",
  PRICE_ALERTS_SUCCEEDED: "PRICE_ALERTS_SUCCEEDED",
  PRICE_ALERTS_FAILED: "PRICE_ALERTS_FAILED",
  TAGS_SUCCEEDED: "TAGS_SUCCEEDED",
  TAGS_FAILED: "TAGS_FAILED",
  RECOMMENDATIONS_LOADING: "RECOMMENDATIONS_LOADING",
  RECOMMENDATIONS_APPENDED: "RECOMMENDATIONS_APPENDED",
  RECOMMENDATIONS_FAILED: "RECOMMENDATIONS_FAILED"
} as const;

type SecondaryFields = {
  financialSituation: AsyncSlot<FinancialSituation>;
  hasMediaMonitor: boolean;
  priceAlerts: AsyncSlot<PriceAlert[]>;
  stockTags: AsyncSlot<StockTag[]>;
  recommendations: AsyncSlot<StockRecommendation[]>;
  hasMoreRecommendations: boolean;
  moreRecommendationsLoading: boolean;
};

export type StockPageState = {
  appliedKey: string;
  pageData: AsyncSlot<StockPageData>;
  stock: AsyncSlot<StockPayload>;
} & SecondaryFields;

const secondaryIdle: SecondaryFields = {
  financialSituation: emptySlot(),
  hasMediaMonitor: false,
  priceAlerts: emptySlot(),
  stockTags: emptySlot(),
  recommendations: emptySlot(),
  hasMoreRecommendations: false,
  moreRecommendationsLoading: false
};

/** Secondary batch only — financial situation is loaded on section open. */
const secondaryLoading = {
  hasMediaMonitor: false,
  priceAlerts: emptySlot(true),
  stockTags: emptySlot(true),
  recommendations: emptySlot(true),
  hasMoreRecommendations: false,
  moreRecommendationsLoading: false
};

export function createInitialStockPageState(
  key: string,
  active: boolean
): StockPageState {
  return {
    appliedKey: key,
    pageData: emptySlot(active),
    stock: emptySlot(active),
    ...secondaryIdle,
    ...(active ? secondaryLoading : {})
  };
}

type StockPageAction =
  | {
      type: typeof STOCK_PAGE_ACTION.RESET_ENTRY;
      key: string;
      active: boolean;
    }
  | { type: typeof STOCK_PAGE_ACTION.PRIMARY_LOADING }
  | {
      type: typeof STOCK_PAGE_ACTION.PRIMARY_SETTLED;
      pageResult: PromiseSettledResult<StockPageData>;
      stockResult: PromiseSettledResult<StockPayload>;
    }
  | { type: typeof STOCK_PAGE_ACTION.SECONDARY_LOADING }
  | {
      type: typeof STOCK_PAGE_ACTION.SECONDARY_SETTLED;
      mediaResult: PromiseSettledResult<MediaMonitorData>;
      alertsResult: PromiseSettledResult<PriceAlert[]>;
      tagsResult: PromiseSettledResult<StockTag[]>;
      recommendationsResult: PromiseSettledResult<StockRecommendationsPayload>;
    }
  | { type: typeof STOCK_PAGE_ACTION.FINANCIAL_SITUATION_LOADING }
  | {
      type: typeof STOCK_PAGE_ACTION.FINANCIAL_SITUATION_SUCCEEDED;
      data: FinancialSituation;
    }
  | {
      type: typeof STOCK_PAGE_ACTION.FINANCIAL_SITUATION_FAILED;
      error: unknown;
    }
  | {
      type: typeof STOCK_PAGE_ACTION.PRICE_ALERTS_SUCCEEDED;
      alerts: PriceAlert[];
    }
  | { type: typeof STOCK_PAGE_ACTION.PRICE_ALERTS_FAILED; error: unknown }
  | { type: typeof STOCK_PAGE_ACTION.TAGS_SUCCEEDED; tags: StockTag[] }
  | { type: typeof STOCK_PAGE_ACTION.TAGS_FAILED; error: unknown }
  | { type: typeof STOCK_PAGE_ACTION.RECOMMENDATIONS_LOADING }
  | {
      type: typeof STOCK_PAGE_ACTION.RECOMMENDATIONS_APPENDED;
      recommendations: StockRecommendation[];
      hasMore: boolean;
    }
  | { type: typeof STOCK_PAGE_ACTION.RECOMMENDATIONS_FAILED; error: unknown };

function slotFromSettled<T>(result: PromiseSettledResult<T>): AsyncSlot<T> {
  if (result.status === PROMISE_SETTLED_STATUS.FULFILLED) {
    return { data: result.value, loading: false, error: null };
  }

  return { data: null, loading: false, error: result.reason };
}

function hasMediaMonitorFromResult(
  result: PromiseSettledResult<MediaMonitorData>
): boolean {
  if (result.status !== PROMISE_SETTLED_STATUS.FULFILLED) {
    return false;
  }

  return (result.value.messages || []).length > 0;
}

function withSecondary(
  state: StockPageState,
  patch: Partial<SecondaryFields>
): StockPageState {
  return {
    ...state,
    ...patch
  };
}

export function stockPageReducer(
  state: StockPageState,
  action: StockPageAction
): StockPageState {
  switch (action.type) {
    case STOCK_PAGE_ACTION.RESET_ENTRY:
      return createInitialStockPageState(action.key, action.active);

    case STOCK_PAGE_ACTION.PRIMARY_LOADING:
      return {
        ...state,
        pageData: emptySlot(true),
        stock: emptySlot(true)
      };

    case STOCK_PAGE_ACTION.PRIMARY_SETTLED: {
      const stockOk =
        action.stockResult.status === PROMISE_SETTLED_STATUS.FULFILLED;

      return {
        ...state,
        pageData: slotFromSettled(action.pageResult),
        stock: slotFromSettled(action.stockResult),
        ...(stockOk ? {} : secondaryIdle)
      };
    }

    case STOCK_PAGE_ACTION.SECONDARY_LOADING:
      return {
        ...state,
        ...secondaryLoading
      };

    case STOCK_PAGE_ACTION.SECONDARY_SETTLED: {
      const recommendations = slotFromRecommendations(
        action.recommendationsResult
      );

      return {
        ...state,
        hasMediaMonitor: hasMediaMonitorFromResult(action.mediaResult),
        priceAlerts: slotFromSettled(action.alertsResult),
        stockTags: slotFromSettled(action.tagsResult),
        recommendations: recommendations.slot,
        hasMoreRecommendations: recommendations.hasMore,
        moreRecommendationsLoading: false
      };
    }

    case STOCK_PAGE_ACTION.FINANCIAL_SITUATION_LOADING:
      return withSecondary(state, {
        financialSituation: {
          data: state.financialSituation.data,
          loading: true,
          error: null
        }
      });

    case STOCK_PAGE_ACTION.FINANCIAL_SITUATION_SUCCEEDED:
      return withSecondary(state, {
        financialSituation: {
          data: action.data,
          loading: false,
          error: null
        }
      });

    case STOCK_PAGE_ACTION.FINANCIAL_SITUATION_FAILED:
      return withSecondary(state, {
        financialSituation: {
          data: state.financialSituation.data,
          loading: false,
          error: action.error
        }
      });

    case STOCK_PAGE_ACTION.PRICE_ALERTS_SUCCEEDED:
      return withSecondary(state, {
        priceAlerts: { data: action.alerts, loading: false, error: null }
      });

    case STOCK_PAGE_ACTION.PRICE_ALERTS_FAILED:
      return withSecondary(state, {
        priceAlerts: {
          data: state.priceAlerts.data,
          loading: false,
          error: action.error
        }
      });

    case STOCK_PAGE_ACTION.TAGS_SUCCEEDED:
      return withSecondary(state, {
        stockTags: { data: action.tags, loading: false, error: null }
      });

    case STOCK_PAGE_ACTION.TAGS_FAILED:
      return withSecondary(state, {
        stockTags: {
          data: state.stockTags.data,
          loading: false,
          error: action.error
        }
      });

    case STOCK_PAGE_ACTION.RECOMMENDATIONS_LOADING:
      return withSecondary(state, {
        moreRecommendationsLoading: true
      });

    case STOCK_PAGE_ACTION.RECOMMENDATIONS_APPENDED:
      return withSecondary(state, {
        recommendations: {
          data: [
            ...(state.recommendations.data || []),
            ...action.recommendations
          ],
          loading: false,
          error: null
        },
        hasMoreRecommendations: action.hasMore,
        moreRecommendationsLoading: false
      });

    case STOCK_PAGE_ACTION.RECOMMENDATIONS_FAILED:
      return withSecondary(state, {
        moreRecommendationsLoading: false,
        recommendations: {
          ...state.recommendations,
          error: action.error
        }
      });

    default:
      return state;
  }
}

function slotFromRecommendations(
  result: PromiseSettledResult<StockRecommendationsPayload>
): { slot: AsyncSlot<StockRecommendation[]>; hasMore: boolean } {
  if (result.status === PROMISE_SETTLED_STATUS.FULFILLED) {
    return {
      slot: {
        data: result.value.recommendations,
        loading: false,
        error: null
      },
      hasMore: result.value.more_recommendations
    };
  }

  return {
    slot: { data: null, loading: false, error: result.reason },
    hasMore: false
  };
}
